// Copyright (C) 2021 Bystronic Laser AG

import {
  ValidationRuleWithParams,
  ValidatorFn,
  ValidationRule,
  ValidationRuleWithoutParams,
} from '@vuelidate/core';
import { User, UserAuthenticationType } from '@/models/user';
import i18n from '@/i18n';
import {
  createI18nMessage,
  helpers,
  TranslationFunction,
} from '@vuelidate/validators';
import * as validators from '@vuelidate/validators';
import { isEmpty, isNil } from './misc';
import { Ref, unref } from 'vue';

const t = i18n.t.bind(i18n) as typeof TranslationFunction;
const withI18nMessage = createI18nMessage({
  t,
  messagePath: ({ $validator }) => `validation.${$validator}`,
});

export const required = withI18nMessage(validators.required);
export const maxLength = withI18nMessage(validators.maxLength, {
  withArguments: true,
});
export const minLength = withI18nMessage(validators.minLength, {
  withArguments: true,
});
export const email = withI18nMessage(validators.email);
export const sameAs = withI18nMessage(validators.sameAs, {
  withArguments: true,
});
export const or = withI18nMessage(validators.or, { withArguments: true });
export const requiredIf = withI18nMessage(validators.requiredIf, {
  withArguments: true,
});
export const numeric = withI18nMessage(validators.numeric);
export const minValue = withI18nMessage(validators.minValue, {
  withArguments: true,
});
export const maxValue = withI18nMessage(validators.maxValue, {
  withArguments: true,
});

function generateMaxDateValidator(
  maxValue: Ref<Date> | Date,
  orEqual = false,
): ValidatorFn<Date> {
  return (value) => {
    const unrefMaxValue = unref(maxValue);
    if (isNil(value) || isNil(unrefMaxValue)) return true;
    if (orEqual) return value <= unrefMaxValue;
    return value < unrefMaxValue;
  };
}

function generateMinDateValidator(
  minValue: Ref<Date> | Date,
  orEqual = false,
): ValidatorFn<Date> {
  return (value) => {
    const unrefMinValue = unref(minValue);
    if (isNil(value) || isNil(unrefMinValue)) return false;
    if (orEqual) return value >= unrefMinValue;
    return value > unrefMinValue;
  };
}

/**
 * Validator for dates that must be strictly greater than the specified date.
 *
 * It also treats as valid null or undefined values. Use a separate required
 * validator if you don't want to allow them.
 *
 * @param maxValue Maximum exclusive date. If not defined, treat the value as
 *   valid, given that there isn't a maximum.
 */
export function exclusiveMaxDate(maxValue: ValidatorParameter<Date>): ValidationRuleWithParams<{
  maxValue: ValidatorParameter<Date>;
  fieldName?: string;
}> {
  return {
    $validator: (value: NilableValue<Date>) => {
      const unrefedMaxValue = unref(maxValue);

      if (isNil(value) || isNil(unrefedMaxValue)) {
        return true;
      }

      return validators.maxValue(value as any)
        && (value < unrefedMaxValue);
    },
    $params: { maxValue },
    $message: ({ $params }) =>
      i18n
        .t('validation.exclusiveMaxDate', {
          fieldName: $params.fieldName,
          maxDate: new Intl.DateTimeFormat().format(unref($params.maxValue!)),
        })
        .toString(),
  };
}

/**
 * Validator for dates that must be greater or equal than the specified date.
 *
 * It also treats as valid null or undefined values. Use a separate required
 * validator if you don't want to allow them.
 *
 * @param maxValue Maximum inclusive date. If not defined, treat the value as
 *   valid, given that there isn't a maximum.
 */
export function maxDate(
  maxValue: ValidatorParameter<Date>,
): ValidationRuleWithParams<{
  maxValue: ValidatorParameter<Date>;
  fieldName?: string;
}> {
  return {
    // maxValue parameter is wrongly typed
    $validator: validators.maxValue(maxValue ?? Number.MAX_VALUE as any).$validator,
    $params: { maxValue },
    $message: ({ $params }) =>
      i18n
        .t('validation.maxDate', {
          fieldName: $params.fieldName,
          maxDate: new Intl.DateTimeFormat().format(unref($params.maxValue!)),
        })
        .toString(),
  };
}

/**
 * Validator for dates that must be strictly less than the specified date.
 *
 * It also treats as valid null or undefined values. Use a separate required
 * validator if you don't want to allow them.
 *
 * @param minValue Minimum exclusive date. If not defined, treat the value as
 *   valid, given that there isn't a minimum.
 */
export function exclusiveMinDate(minValue: ValidatorParameter<Date>): ValidationRuleWithParams<{
  minValue: ValidatorParameter<Date>;
  fieldName?: string;
}> {
  return {
    $validator: (value: NilableValue<Date>) => {
      const unrefedMinValue = unref(minValue);

      if (isNil(value) || isNil(unrefedMinValue)) {
        return true;
      }

      return validators.minValue(value as any)
        && (value > unrefedMinValue);
    },
    $params: { minValue },
    $message: ({ $params }) =>
      i18n
        .t('validation.exclusiveMinDate', {
          fieldName: $params.fieldName,
          minDate: new Intl.DateTimeFormat().format(unref($params.minValue!)),
        })
        .toString(),
  };
}

/**
 * Validator for dates that must be less or equal than the specified date.
 *
 * It also treats as valid null or undefined values. Use a separate required
 * validator if you don't want to allow them.
 *
 * @param minValue Minimum inclusive date. If not defined, treat the value as
 *   valid, given that there isn't a minimum.
 */
export function minDate(
  minValue: ValidatorParameter<Date>,
): ValidationRuleWithParams<{
  minValue: ValidatorParameter<Date>;
  fieldName?: string;
}> {
  return {
    // minValue parameter is wrongly typed
    $validator: validators.minValue(minValue ?? Number.MIN_VALUE as any).$validator,
    $params: { minValue },
    $message: ({ $params }) =>
      i18n
        .t('validation.minDate', {
          fieldName: $params.fieldName,
          minDate: new Intl.DateTimeFormat().format(unref($params.minValue!)),
        })
        .toString(),
  };
}

export type ValidationRules<T = any> = Record<string, ValidationRule<T>>;

export const withLocalizedValidators = (
  key: string,
  validators: ValidationRules,
): ValidationRules => {
  return Object.fromEntries(
    Object.entries(validators).map(([name, validator]) => [
      name,
      helpers.withParams({ fieldName: i18n.t(key).toString() }, validator),
    ]),
  );
};

export function passwordsMatch(
  otherPassword: Ref<string> | string,
): ValidationRuleWithParams {
  const $validator: ValidatorFn<string, User> = (value, siblings) => {
    return (
      (!isNil(siblings.authenticationType) &&
        siblings.authenticationType !== UserAuthenticationType.Local) ||
      (unref(value) === unref(otherPassword) && !isEmpty(value))
    );
  };
  return {
    $validator,
    $message: () => i18n.t('validation.password_repeat').toString(),
    $params: {
      otherPassword,
    },
  };
}

export function optional<T, P extends object = object>(
  validator: ValidationRuleWithParams<P, T> | ValidationRuleWithoutParams<T>,
): ValidationRule {
  const $params = {
    ...('$params' in validator ? validator.$params : {}),
    validator,
  };
  return {
    ...validator,
    $validator: (value, siblings, vm) => {
      if (!helpers.req(value)) {
        return true;
      }
      return validator.$validator(value, siblings, vm);
    },
    $params,
  };
}

type ValidatorParameter<T> = T | Ref<T> | null | undefined;
type NilableValue<T> = T | null | undefined;
